import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { isTooManyRequestsError, storableError } from '../../util/errors';
import { retryOnceOn429 } from '../../util/retryOnceOn429';
import { retryOn429 } from '../../util/sharetribeSafeQuery';
import { markClientRateLimited } from '../../util/sharetribeRateLimit';
import { listingHasKnownPayoutsReady } from '../../util/stripeErrors';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  transactionLineItems,
  gateSessionText,
  logChatContactBlock,
  fetchListingReviews,
} from '../../util/api';
import { detectContactShare } from '../../util/chatContactFilter';
import { firstContactFlagCategory, redactContactSnippet } from '../../util/contactRedact';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  attachAuthorProfileListings,
  denormaliseReviewQuery,
  hydrateReviewAuthor,
  reviewAuthorImageQuery,
} from '../../util/reviewAuthor';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  parse,
} from '../../util/urlHelpers';
import { wantsBookATimeSheet } from '../../util/bookingIntent';
import {
  getProcess,
  isBookingProcessAlias,
  isNegotiationProcessAlias,
  OFFER,
} from '../../transactions/transaction';
import { fetchCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';
import {
  isUserIdentityVerified,
  isListingAuthorVerified,
  identityRequiredError,
} from '../../util/identity';
import { pickLeaveReviewTransaction } from '../../util/leaveReview';
import { SESSION_LENGTH_MINUTES, TALK_SESSION_LISTING_TYPE } from '../../util/sessionPricing';

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
const showListingPayloadCreator = ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Skip a second currentUser.show when boot or a sibling page already loaded it.
  const alreadyHaveUser = !!thunkAPI.getState?.()?.user?.currentUser;
  if (!alreadyHaveUser) {
    const fetchCurrentUserOptions = {
      updateHasListings: false,
      updateNotifications: false,
    };
    dispatch(fetchCurrentUser(fetchCurrentUserOptions));
  }

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = () => (isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params));

  return retryOnceOn429(show)
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      if (isTooManyRequestsError(e)) {
        e.status = e.status || 429;
        e.code = e.code || 'rate_limited';
        markClientRateLimited();
      }
      return rejectWithValue(storableError(e));
    });
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();
};

///////////////////
// Fetch Reviews //
///////////////////
export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ listingId }, { rejectWithValue, extra: sdk }) => {
    const marketplacePublic = () =>
      sdk.reviews
        .query({
          listing_id: listingId,
          type: 'ofProvider',
          state: 'public',
          ...reviewAuthorImageQuery(),
        })
        .then(response =>
          attachAuthorProfileListings(sdk, denormaliseReviewQuery(response).map(hydrateReviewAuthor))
        );

    return fetchListingReviews(listingId?.uuid || listingId)
      .then(payload => {
        const reviews = payload?.data?.reviews;
        if (Array.isArray(reviews) && reviews.length > 0) {
          return attachAuthorProfileListings(sdk, reviews.map(hydrateReviewAuthor));
        }
        return marketplacePublic();
      })
      .catch(() => marketplacePublic())
      .catch(e => {
        if (isTooManyRequestsError(e)) {
          markClientRateLimited();
        }
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  return dispatch(fetchReviewsThunk({ listingId })).unwrap();
};

const authorIdFromListing = listing =>
  listing?.author?.id?.uuid ||
  listing?.relationships?.author?.data?.id?.uuid ||
  listing?.relationships?.author?.data?.id ||
  null;

/**
 * Find a completed, still-unreviewed order this viewer has with this listener.
 * Failures stay quiet so a stranger or a network blip never blocks the page.
 */
export const fetchLeaveReviewOfferThunk = createAsyncThunk(
  'ListingPage/fetchLeaveReviewOffer',
  ({ listing, currentUser }, { rejectWithValue, extra: sdk }) => {
    const authorId = authorIdFromListing(listing);
    const listingId = listing?.id?.uuid || listing?.id || null;
    const currentUserId = currentUser?.id?.uuid;
    if (
      !authorId ||
      !currentUserId ||
      authorId === currentUserId ||
      listing?.type === 'ownListing'
    ) {
      return { transactionId: null };
    }

    return sdk.transactions
      .query({
        only: 'order',
        userId: authorId,
        include: ['listing', 'provider', 'customer'],
        page: 1,
        perPage: 25,
      })
      .then(response => {
        const txs = denormalisedResponseEntities(response);
        const picked = pickLeaveReviewTransaction(txs, {
          listingId,
          providerId: authorId,
          currentUserId,
        });
        return { transactionId: picked?.id?.uuid || null };
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);

//////////////////////
// Fetch Time Slots //
//////////////////////

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) => {
    return retryOn429(() => sdk.timeslots.query(params), { retries: 1, jitter: 0 }).then(response =>
      denormalisedResponseEntities(response)
    );
  }
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams })).unwrap();
  } else {
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams })).unwrap();
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();
};

//////////////////
// Send Inquiry //
//////////////////
const sendInquiryPayloadCreator = (
  { listing, message },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  const currentUser = getState()?.user?.currentUser;
  if (!isUserIdentityVerified(currentUser) || !isListingAuthorVerified(listing)) {
    return rejectWithValue({
      ...storableError(
        identityRequiredError('Both people must complete identity verification before messaging.')
      ),
      code: 'identity_required',
    });
  }
  const contactCategories = detectContactShare(message);
  if (contactCategories.length > 0) {
    logChatContactBlock({
      sessionId: listing?.id?.uuid,
      transactionId: listing?.id?.uuid,
      categories: contactCategories,
      signals: contactCategories,
      excerptRedacted: redactContactSnippet(
        message,
        firstContactFlagCategory(contactCategories) || contactCategories[0]
      ),
    }).catch(() => {});
    return rejectWithValue({
      code: 'content_blocked',
      categories: contactCategories,
      message: 'That message was blocked.',
    });
  }
  return gateSessionText(message)
    .then(response => {
      const data = response?.data || {};
      if (data.allowed === false) {
        return rejectWithValue({
          code: 'content_blocked',
          categories: data.categories,
          message: 'That message was blocked.',
        });
      }
      const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
      if (!processAlias) {
        const error = new Error('No transaction process attached to listing');
        log.error(error, 'listing-process-missing', {
          listingId: listing?.id?.uuid,
        });
        return rejectWithValue(storableError(error));
      }

      const listingId = listing?.id;
      const [processName, alias] = processAlias.split('/');

      // Negotiation process does not support inquiry for customer role on OFFER flog (only request for quote is supported)
      const isNegotiationProcess = isNegotiationProcessAlias(processAlias);
      const unitType = listing?.attributes?.publicData?.unitType || '';
      if (isNegotiationProcess && unitType === OFFER) {
        return rejectWithValue(
          storableError(
            new Error(
              'Negotiation process with unit type OFFER does not support inquiry for customer role'
            )
          )
        );
      }

      const transitions = getProcess(processName)?.transitions;

      const bodyParams = {
        transition: transitions.INQUIRE,
        processAlias,
        params: { listingId },
      };
      return sdk.transactions
        .initiate(bodyParams)
        .then(response => {
          const transactionId = response.data.data.id;

          // Send the message to the created transaction
          return sdk.messages.send({ transactionId, content: message }).then(() => {
            dispatch(setCurrentUserHasOrders());
            return transactionId;
          });
        })
        .catch(e => {
          return rejectWithValue(storableError(e));
        });
    })
    .catch(e => {
      if (e?.code === 'content_blocked') {
        return rejectWithValue(e);
      }
      return rejectWithValue(storableError(e));
    });
};

export const sendInquiryThunk = createAsyncThunk(
  'ListingPage/sendInquiry',
  sendInquiryPayloadCreator
);
// Backward compatible wrapper for the thunk
export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  return dispatch(sendInquiryThunk({ listing, message })).unwrap();
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const listingType = publicData?.listingType;
  const isTalkSession = listingType === TALK_SESSION_LISTING_TYPE;
  const tz = availabilityPlan?.timezone || (isTalkSession ? 'America/New_York' : null);

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { priceVariants } = publicData || {};
    const unitType = publicData?.unitType || (isTalkSession ? 'fixed' : publicData?.unitType);
    const startTimeInterval =
      publicData?.startTimeInterval || (isTalkSession ? 'quarterHour' : undefined);
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const variantMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);
    const bookingLengthInMinutes =
      Number.isFinite(variantMinutes) && variantMinutes !== Number.MAX_SAFE_INTEGER
        ? variantMinutes
        : isTalkSession
        ? SESSION_LENGTH_MINUTES
        : variantMinutes;

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

//////////////////////////////////
// Fetch Transaction Line Items //
//////////////////////////////////
const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data;
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  leaveReviewTransactionId: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
};

const listingPageSlice = createSlice({
  name: 'ListingPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        // Data is handled by addMarketplaceEntities in the thunk
        state.id = action.payload.data.data.id;
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = action.payload;
      })
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchLeaveReviewOfferThunk.fulfilled, (state, action) => {
        state.leaveReviewTransactionId = action.payload?.transactionId || null;
      })
      .addCase(fetchLeaveReviewOfferThunk.rejected, state => {
        state.leaveReviewTransactionId = null;
      })
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })
      .addCase(sendInquiryThunk.pending, state => {
        state.sendInquiryInProgress = true;
        state.sendInquiryError = null;
      })
      .addCase(sendInquiryThunk.fulfilled, state => {
        state.sendInquiryInProgress = false;
        state.inquiryModalOpenForListingId = null;
      })
      .addCase(sendInquiryThunk.rejected, (state, action) => {
        state.sendInquiryInProgress = false;
        state.sendInquiryError = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      });
  },
});

export const { setInitialValues } = listingPageSlice.actions;

export default listingPageSlice.reducer;

/**
 * Reuse a listing already in the store (SSR hydrate or search card) so a
 * client-side listing navigate does not spend another listings.show.
 *
 * @param {Object} state
 * @param {Object} listingId
 * @returns {Object|null}
 */
export const listingFromMarketplaceStore = (state, listingId) => {
  const [listing] = getMarketplaceEntities(state, [{ id: listingId, type: 'listing' }]);
  return listing?.attributes?.title ? listing : null;
};

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // Soft browse: guests can open a published Listening profile.
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !currentUser || !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const cachedListing = listingFromMarketplaceStore(getState(), listingId);
  // Search cards omit publicData.payoutsReady. Reusing them made signed-in
  // talkers see “not set up to take bookings” after a home/search click.
  const canSkipShow =
    !hasNoViewingRights && !!cachedListing && listingHasKnownPayoutsReady(cachedListing);

  const searchParams = parse(search || '');
  const wantsCalendar = wantsBookATimeSheet(searchParams);

  const showPromise = canSkipShow
    ? Promise.resolve({ data: { data: cachedListing } })
    : dispatch(showListing(listingId, config, hasNoViewingRights));

  return showPromise
    .then(response => {
      const listing = response?.data?.data || cachedListing;
      // Reviews are optional. Never fail the page (or spend a parallel
      // listings.show) when reviews.query 429s.
      const reviewsPromise = hasNoViewingRights
        ? Promise.resolve()
        : dispatch(fetchReviews(listingId)).catch(() => {});
      const transactionProcessAlias =
        listing?.attributes?.publicData?.transactionProcessAlias || '';
      // Timeslots wait until Book a time is open. Start now listing view must
      // not query availability on mount — that 429s Sharetribe.
      if (wantsCalendar && isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
        fetchMonthlyTimeSlots(dispatch, listing);
      }
      return reviewsPromise.then(() => response);
    })
    .catch(e => {
      if (isTooManyRequestsError(e)) {
        markClientRateLimited();
        if (cachedListing) {
          return { data: { data: cachedListing } };
        }
      }
      throw e;
    });
};
